import React, { useEffect, useRef, useState } from "react";
import { api, downloadPdf, formatMoney, formatDate, todayISO } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DatePicker from "@/components/DatePicker";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ShoppingBasket, Wallet, Camera, ScanLine, Trash, ChevronLeft, FileDown } from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  fecha: todayISO(),
  insumo: "",
  cantidad: 1,
  unidad: "u",
  precio_unitario: 0,
  descuento: 0,
  proveedor: "",
  nota: "",
};

export default function Insumos() {
  const [rows, setRows] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filters, setFilters] = useState({ desde: "", hasta: "", insumo: "" });

  // OCR state
  const ocrFileRef = useRef(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [ocrItems, setOcrItems] = useState([]);
  const [ocrMeta, setOcrMeta] = useState({ proveedor: "", fecha: todayISO(), descuento_global: 0 });
  const [ocrSaving, setOcrSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.desde) params.desde = filters.desde;
      if (filters.hasta) params.hasta = filters.hasta;
      if (filters.insumo) params.insumo = filters.insumo;
      const [a, b] = await Promise.all([
        api.get("/insumos", { params }),
        api.get("/insumos/resumen/totales", { params }),
      ]);
      setRows(a.data);
      setResumen(b.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      fecha: it.fecha,
      insumo: it.insumo,
      cantidad: it.cantidad,
      unidad: it.unidad,
      precio_unitario: it.precio_unitario,
      descuento: it.descuento || 0,
      proveedor: it.proveedor || "",
      nota: it.nota || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.insumo.trim()) { toast.error("El nombre del insumo es obligatorio."); return; }
    const payload = {
      ...form,
      cantidad: Number(form.cantidad),
      precio_unitario: Number(form.precio_unitario),
      descuento: Number(form.descuento) || 0,
    };
    try {
      if (editing) await api.put(`/insumos/${editing.id}`, payload);
      else await api.post("/insumos", payload);
      toast.success("Compra guardada.");
      setDialogOpen(false);
      load();
    } catch { toast.error("Error al guardar."); }
  };

  const remove = async (it) => {
    try { await api.delete(`/insumos/${it.id}`); toast.success("Compra eliminada."); load(); }
    catch { toast.error("Error al eliminar."); }
  };

  const totalLive = Math.max(0, Number(form.cantidad || 0) * Number(form.precio_unitario || 0) - Number(form.descuento || 0));

  const [pdfFormato, setPdfFormato] = useState("cronologico");
  const [pdfBusy, setPdfBusy] = useState(false);
  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const params = new URLSearchParams();
      if (filters.desde) params.set("desde", filters.desde);
      if (filters.hasta) params.set("hasta", filters.hasta);
      if (filters.insumo) params.set("insumo", filters.insumo);
      params.set("formato", pdfFormato);
      await downloadPdf(
        `/insumos/export/pdf?${params.toString()}`,
        `gastos_${filters.desde || "inicio"}_${filters.hasta || "hoy"}_${pdfFormato}.pdf`,
      );
    } catch {
      toast.error("Error al exportar PDF.");
    } finally { setPdfBusy(false); }
  };

  // ============ OCR ============
  const handleOcrPick = () => ocrFileRef.current?.click();

  const handleOcrFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/insumos/ocr", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const items = (res.data.items || []).map((it) => ({
        ...it,
        descuento: Number(it.descuento) || 0,
        _key: `ocr-${Math.random().toString(36).slice(2)}`,
      }));
      setOcrItems(items);
      setOcrMeta({
        proveedor: res.data.proveedor || "",
        fecha: res.data.fecha && /^\d{4}-\d{2}-\d{2}$/.test(res.data.fecha) ? res.data.fecha : todayISO(),
        descuento_global: 0,
      });
      setOcrOpen(true);
      if (items.length === 0) {
        toast.info("La imagen se procesó pero no se detectaron items. Cargalos manualmente.");
      } else {
        toast.success(`Detectamos ${items.length} item${items.length !== 1 ? "s" : ""}. Revisalos antes de guardar.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al procesar la imagen.");
    } finally {
      setOcrLoading(false);
      if (ocrFileRef.current) ocrFileRef.current.value = "";
    }
  };

  const ocrUpdate = (key, patch) => {
    setOcrItems((prev) => prev.map((it) => {
      if (it._key !== key) return it;
      const next = { ...it, ...patch };
      const cantidad = Number(next.cantidad || 0);
      const punit = Number(next.precio_unitario || 0);
      // recompute total if user edited cantidad/precio_unitario
      if (patch.cantidad !== undefined || patch.precio_unitario !== undefined) {
        next.precio_total = Math.round(cantidad * punit * 100) / 100;
      }
      return next;
    }));
  };

  const ocrRemove = (key) => setOcrItems((prev) => prev.filter((it) => it._key !== key));
  const ocrAdd = () => setOcrItems((prev) => [...prev, {
    _key: `new-${Date.now()}`,
    insumo: "", cantidad: 1, unidad: "u",
    precio_unitario: 0, precio_total: 0, descuento: 0,
  }]);

  // Compute global discount distribution
  const ocrSubtotal = ocrItems.reduce((s, it) => s + (Number(it.precio_total) || 0), 0);
  const descGlobal = Math.max(0, Number(ocrMeta.descuento_global) || 0);
  const ocrTotal = Math.max(0, ocrSubtotal - descGlobal);

  const ocrSave = async () => {
    if (ocrItems.length === 0) { toast.error("Agregá al menos un item."); return; }
    const valid = ocrItems.filter((it) => (it.insumo || "").trim() && Number(it.cantidad) > 0);
    if (valid.length === 0) {
      toast.error("Cada item necesita nombre y cantidad.");
      return;
    }
    setOcrSaving(true);
    try {
      // Distribute global discount proportionally based on each item's gross subtotal
      const grossSubtotal = valid.reduce(
        (s, it) => s + (Number(it.cantidad) * Number(it.precio_unitario || (Number(it.precio_total) / Math.max(1, Number(it.cantidad))))),
        0,
      );
      const globalDesc = Math.max(0, Number(ocrMeta.descuento_global) || 0);
      await Promise.all(valid.map((it) => {
        const cantidad = Number(it.cantidad);
        const pu = Number(it.precio_unitario) || (Number(it.precio_total) / Math.max(1, cantidad));
        const itemGross = cantidad * pu;
        const itemDescItem = Math.max(0, Number(it.descuento) || 0);
        const itemDescShare = grossSubtotal > 0 ? globalDesc * (itemGross / grossSubtotal) : 0;
        const totalDesc = Math.round((itemDescItem + itemDescShare) * 100) / 100;
        return api.post("/insumos", {
          fecha: ocrMeta.fecha,
          insumo: it.insumo.trim(),
          cantidad,
          unidad: it.unidad || "u",
          precio_unitario: pu,
          descuento: totalDesc,
          proveedor: ocrMeta.proveedor || "",
          nota: "Cargado por foto",
        });
      }));
      toast.success(`Guardé ${valid.length} compras.`);
      setOcrOpen(false);
      setOcrItems([]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar.");
    } finally {
      setOcrSaving(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Costos" title="Insumos / Compras" subtitle="Cargá los gastos del día: ingredientes, packaging, lo que necesites.">
        <input ref={ocrFileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleOcrFile} className="hidden" data-testid="ins-ocr-file" />
        <div className="flex flex-wrap gap-1.5 items-center">
          <Select value={pdfFormato} onValueChange={setPdfFormato}>
            <SelectTrigger className="h-10 w-auto sm:w-[150px] bg-white" data-testid="ins-pdf-formato">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cronologico">PDF Cronológico</SelectItem>
              <SelectItem value="proveedor">PDF por Proveedor</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="border-[#E5E0D8] hover:bg-[#F9F6F0] gap-2 h-10" onClick={exportPdf} disabled={pdfBusy} data-testid="ins-pdf-btn">
            <FileDown className="h-4 w-4" />
            <span className="hidden sm:inline">{pdfBusy ? "Generando..." : "Exportar PDF"}</span>
            <span className="sm:hidden">PDF</span>
          </Button>
          <Button variant="outline" className="border-[#E5E0D8] hover:bg-[#F9F6F0] gap-2 h-10" onClick={handleOcrPick} disabled={ocrLoading} data-testid="ins-ocr-btn">
            <Camera className="h-4 w-4" />
            <span className="hidden sm:inline">{ocrLoading ? "Leyendo..." : "Cargar por foto"}</span>
            <span className="sm:hidden">Foto</span>
          </Button>
          <Button className="bg-[#C85A32] hover:bg-[#A64A29] text-white gap-2 h-10" onClick={openNew} data-testid="ins-nuevo">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Manual</span>
            <span className="sm:hidden">+</span>
          </Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5 border-[#E5E0D8] rounded-2xl bg-white">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C]">Total gastado</div>
            <Wallet className="h-5 w-5 text-[#C85A32]" />
          </div>
          <div className="font-display font-bold text-3xl text-[#1A1A1A]" data-testid="ins-total">{formatMoney(resumen?.total)}</div>
          <div className="text-xs text-[#595959] mt-1">{resumen?.cantidad_compras || 0} compras en el período</div>
        </Card>

        <Card className="p-5 border-[#E5E0D8] rounded-2xl bg-white md:col-span-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-[11px] text-[#595959] mb-1.5 block">Desde</Label>
              <DatePicker value={filters.desde} onChange={(v) => setFilters((f) => ({ ...f, desde: v }))} testid="ins-f-desde" />
            </div>
            <div>
              <Label className="text-[11px] text-[#595959] mb-1.5 block">Hasta</Label>
              <DatePicker value={filters.hasta} onChange={(v) => setFilters((f) => ({ ...f, hasta: v }))} testid="ins-f-hasta" />
            </div>
            <div>
              <Label className="text-[11px] text-[#595959] mb-1.5 block">Insumo</Label>
              <Input value={filters.insumo} onChange={(e) => setFilters((f) => ({ ...f, insumo: e.target.value }))} placeholder="Buscar..." className="bg-white h-10" data-testid="ins-f-insumo" />
            </div>
            <Button variant="outline" className="border-[#E5E0D8] hover:bg-[#F9F6F0] h-10" onClick={() => setFilters({ desde: "", hasta: "", insumo: "" })}>Limpiar</Button>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-2 border-[#E5E0D8] rounded-2xl bg-white overflow-hidden">
          {/* Desktop: table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#F9F6F0] border-b border-[#E5E0D8]">
                <tr className="text-[11px] uppercase tracking-[0.14em] text-[#8C8C8C]">
                  <th className="px-5 py-3">Fecha</th>
                  <th className="px-5 py-3">Insumo</th>
                  <th className="px-5 py-3 text-right">Cant.</th>
                  <th className="px-5 py-3">Unidad</th>
                  <th className="px-5 py-3 text-right">P. Unit.</th>
                  <th className="px-5 py-3 text-right">Desc.</th>
                  <th className="px-5 py-3 text-right">Total</th>
                  <th className="px-5 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="text-center text-[#8C8C8C] py-12">Cargando...</td></tr>}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-16">
                    <ShoppingBasket className="h-10 w-10 text-[#C85A32] mx-auto mb-3 opacity-50" />
                    <div className="font-display font-semibold text-lg text-[#1A1A1A]">Sin compras cargadas</div>
                  </td></tr>
                )}
                {!loading && rows.map((it, i) => (
                  <tr key={it.id} className="border-b border-[#F0EBE0] hover:bg-[#FBF8F2] fade-row" style={{ animationDelay: `${i * 20}ms` }} data-testid={`ins-row-${it.id}`}>
                    <td className="px-5 py-4 text-sm">{formatDate(it.fecha)}</td>
                    <td className="px-5 py-4 font-medium">{it.insumo}</td>
                    <td className="px-5 py-4 text-right font-mono">{it.cantidad}</td>
                    <td className="px-5 py-4 text-sm">{it.unidad}</td>
                    <td className="px-5 py-4 text-right font-mono">{formatMoney(it.precio_unitario)}</td>
                    <td className="px-5 py-4 text-right font-mono text-[#A6751F]">
                      {Number(it.descuento) > 0 ? `-${formatMoney(it.descuento)}` : "—"}
                    </td>
                    <td className="px-5 py-4 text-right font-mono font-semibold text-[#C85A32]">{formatMoney(it.total)}</td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" className="text-[#595959] hover:text-[#C85A32] hover:bg-[#F4EAE6]" onClick={() => openEdit(it)} data-testid={`ins-edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-[#8E2E2E] hover:bg-[#FBEBEB]" data-testid={`ins-del-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Eliminar compra</AlertDialogTitle>
                              <AlertDialogDescription>¿Eliminar esta compra de "{it.insumo}"?</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Volver</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(it)} className="bg-[#B23A3A] hover:bg-[#8E2E2E]">Eliminar</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: cards */}
          <div className="md:hidden divide-y divide-[#F0EBE0]">
            {loading && <div className="text-center text-[#8C8C8C] py-12">Cargando...</div>}
            {!loading && rows.length === 0 && (
              <div className="text-center py-12 px-4">
                <ShoppingBasket className="h-10 w-10 text-[#C85A32] mx-auto mb-3 opacity-50" />
                <div className="font-display font-semibold text-base text-[#1A1A1A]">Sin compras cargadas</div>
                <p className="text-xs text-[#595959] mt-1">Tocá "Manual" o "Foto" para empezar.</p>
              </div>
            )}
            {!loading && rows.map((it, i) => (
              <div key={it.id} className="p-4 fade-row" style={{ animationDelay: `${i * 20}ms` }} data-testid={`ins-card-${it.id}`}>
                <div className="flex justify-between items-start gap-2 mb-1.5">
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-[#1A1A1A] truncate">{it.insumo}</div>
                    <div className="text-[11px] text-[#8C8C8C] mt-0.5">
                      {formatDate(it.fecha)} {it.proveedor && <> · {it.proveedor}</>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-bold text-[#C85A32]">{formatMoney(it.total)}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-[11px] text-[#595959]">
                  <div className="font-mono">
                    {it.cantidad} {it.unidad} × {formatMoney(it.precio_unitario)}
                    {Number(it.descuento) > 0 && <span className="text-[#A6751F] ml-1">· -{formatMoney(it.descuento)}</span>}
                  </div>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => openEdit(it)} className="h-8 w-8 rounded-md hover:bg-[#F4EAE6] text-[#595959] flex items-center justify-center" data-testid={`ins-mobile-edit-${it.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button type="button" className="h-8 w-8 rounded-md hover:bg-[#FBEBEB] text-[#8E2E2E] flex items-center justify-center" data-testid={`ins-mobile-del-${it.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminar compra</AlertDialogTitle>
                          <AlertDialogDescription>¿Eliminar esta compra de "{it.insumo}"?</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Volver</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(it)} className="bg-[#B23A3A] hover:bg-[#8E2E2E]">Eliminar</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                {it.nota && <div className="text-[11px] text-[#8C8C8C] mt-1.5 italic">{it.nota}</div>}
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 border-[#E5E0D8] rounded-2xl bg-white">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C] mb-4">Gasto por insumo</div>
          {(!resumen || resumen.por_insumo.length === 0) ? (
            <div className="text-sm text-[#8C8C8C]">Sin datos en el período.</div>
          ) : (
            <ul className="space-y-3">
              {resumen.por_insumo.slice(0, 10).map((r, i) => (
                <li key={r.insumo} className="flex items-center gap-3 fade-row" style={{ animationDelay: `${i * 30}ms` }}>
                  <div className="h-7 w-7 rounded-md bg-[#F4EAE6] text-[#C85A32] flex items-center justify-center text-[12px] font-bold">{i + 1}</div>
                  <div className="flex-1 truncate text-sm font-medium">{r.insumo}</div>
                  <div className="font-mono font-semibold text-sm">{formatMoney(r.total)}</div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>{editing ? "Editar compra" : "Nueva compra"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div>
              <Label>Fecha</Label>
              <DatePicker value={form.fecha} onChange={(v) => setForm({ ...form, fecha: v })} testid="ins-form-fecha" allowClear={false} />
            </div>
            <div>
              <Label>Insumo</Label>
              <Input value={form.insumo} onChange={(e) => setForm({ ...form, insumo: e.target.value })} placeholder="ej: Pan miga" data-testid="ins-form-insumo" />
            </div>
            <div>
              <Label>Cantidad</Label>
              <Input type="number" value={form.cantidad} onChange={(e) => setForm({ ...form, cantidad: e.target.value })} data-testid="ins-form-cant" />
            </div>
            <div>
              <Label>Unidad</Label>
              <Input value={form.unidad} onChange={(e) => setForm({ ...form, unidad: e.target.value })} placeholder="kg, u, lt..." data-testid="ins-form-unidad" />
            </div>
            <div>
              <Label>Precio unitario</Label>
              <Input type="number" value={form.precio_unitario} onChange={(e) => setForm({ ...form, precio_unitario: e.target.value })} data-testid="ins-form-precio" />
            </div>
            <div>
              <Label>Descuento</Label>
              <Input type="number" min="0" value={form.descuento} onChange={(e) => setForm({ ...form, descuento: e.target.value })} placeholder="0" data-testid="ins-form-descuento" />
            </div>
            <div className="col-span-2">
              <Label>Total</Label>
              <div className="h-10 flex items-center px-3 rounded-md border border-[#E5E0D8] bg-[#F9F6F0] font-mono font-semibold text-[#C85A32]" data-testid="ins-form-total">
                {formatMoney(totalLive)}
                {Number(form.descuento) > 0 && (
                  <span className="ml-2 text-[11px] text-[#A6751F] font-sans font-normal">
                    (desc. {formatMoney(form.descuento)})
                  </span>
                )}
              </div>
            </div>
            <div className="col-span-2">
              <Label>Proveedor (opcional)</Label>
              <Input value={form.proveedor} onChange={(e) => setForm({ ...form, proveedor: e.target.value })} data-testid="ins-form-proveedor" />
            </div>
            <div className="col-span-2">
              <Label>Nota (opcional)</Label>
              <Input value={form.nota} onChange={(e) => setForm({ ...form, nota: e.target.value })} data-testid="ins-form-nota" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-[#C85A32] hover:bg-[#A64A29] text-white" onClick={save} data-testid="ins-form-save">{editing ? "Actualizar" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OCR Review Dialog */}
      <Dialog open={ocrOpen} onOpenChange={setOcrOpen}>
        <DialogContent className="sm:max-w-[820px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="h-5 w-5 text-[#C85A32]" /> Revisar items detectados
            </DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-[#595959] mb-2">
            Editá lo que sea necesario antes de guardar. Si algo no es correcto, eliminalo o agregalo manualmente.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div>
              <Label className="text-[12px] text-[#595959] mb-1.5 block">Fecha</Label>
              <DatePicker value={ocrMeta.fecha} onChange={(v) => setOcrMeta({ ...ocrMeta, fecha: v })} testid="ocr-fecha" allowClear={false} />
            </div>
            <div>
              <Label className="text-[12px] text-[#595959] mb-1.5 block">Proveedor (opcional)</Label>
              <Input value={ocrMeta.proveedor} onChange={(e) => setOcrMeta({ ...ocrMeta, proveedor: e.target.value })} placeholder="ej: Carnicería La Plata" data-testid="ocr-proveedor" />
            </div>
            <div>
              <Label className="text-[12px] text-[#595959] mb-1.5 block">Descuento global</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={ocrMeta.descuento_global}
                onChange={(e) => setOcrMeta({ ...ocrMeta, descuento_global: e.target.value })}
                placeholder="0"
                className="font-mono"
                data-testid="ocr-desc-global"
              />
            </div>
          </div>

          {/* Desktop: table */}
          <div className="hidden sm:block border border-[#E5E0D8] rounded-xl overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#F9F6F0] border-b border-[#E5E0D8] text-[10px] uppercase tracking-wider text-[#8C8C8C]">
                <tr>
                  <th className="px-2 py-2">Insumo</th>
                  <th className="px-2 py-2 w-[64px] text-right">Cant.</th>
                  <th className="px-2 py-2 w-[64px]">Unidad</th>
                  <th className="px-2 py-2 w-[96px] text-right">P. Unit.</th>
                  <th className="px-2 py-2 w-[80px] text-right">Desc.</th>
                  <th className="px-2 py-2 w-[100px] text-right">Total</th>
                  <th className="px-2 py-2 w-[44px]"></th>
                </tr>
              </thead>
              <tbody>
                {ocrItems.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-[#8C8C8C] py-6">Sin items. Agregá uno o subí otra foto.</td></tr>
                )}
                {ocrItems.map((it, idx) => (
                  <tr key={it._key} className="border-b border-[#F0EBE0] last:border-b-0" data-testid={`ocr-row-${idx}`}>
                    <td className="px-2 py-1.5">
                      <Input value={it.insumo} onChange={(e) => ocrUpdate(it._key, { insumo: e.target.value })} className="h-9" data-testid={`ocr-row-${idx}-insumo`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="number" min="0" step="0.01" value={it.cantidad} onChange={(e) => ocrUpdate(it._key, { cantidad: e.target.value })} className="h-9 text-right" data-testid={`ocr-row-${idx}-cantidad`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input value={it.unidad} onChange={(e) => ocrUpdate(it._key, { unidad: e.target.value })} className="h-9" data-testid={`ocr-row-${idx}-unidad`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="number" min="0" step="0.01" value={it.precio_unitario} onChange={(e) => ocrUpdate(it._key, { precio_unitario: e.target.value })} className="h-9 text-right font-mono" data-testid={`ocr-row-${idx}-pu`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="number" min="0" step="0.01" value={it.descuento || 0} onChange={(e) => ocrUpdate(it._key, { descuento: e.target.value })} className="h-9 text-right font-mono text-[#A6751F]" data-testid={`ocr-row-${idx}-desc`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="number" min="0" step="0.01" value={it.precio_total} onChange={(e) => ocrUpdate(it._key, { precio_total: e.target.value })} className="h-9 text-right font-mono font-semibold text-[#C85A32]" data-testid={`ocr-row-${idx}-total`} />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <button type="button" onClick={() => ocrRemove(it._key)} className="h-9 w-9 rounded-md hover:bg-[#FBEBEB] text-[#8E2E2E] flex items-center justify-center" aria-label="Quitar" data-testid={`ocr-row-${idx}-remove`}>
                        <Trash className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: cards */}
          <div className="sm:hidden space-y-2">
            {ocrItems.length === 0 && (
              <div className="text-center text-[#8C8C8C] py-6 border border-dashed border-[#E5E0D8] rounded-lg">
                Sin items. Agregá uno o subí otra foto.
              </div>
            )}
            {ocrItems.map((it, idx) => (
              <div key={it._key} className="border border-[#E5E0D8] rounded-xl p-3 space-y-2" data-testid={`ocr-card-${idx}`}>
                <div className="flex items-center gap-2">
                  <Input
                    value={it.insumo}
                    onChange={(e) => ocrUpdate(it._key, { insumo: e.target.value })}
                    placeholder="Nombre"
                    className="h-9"
                    data-testid={`ocr-card-${idx}-insumo`}
                  />
                  <button
                    type="button"
                    onClick={() => ocrRemove(it._key)}
                    className="h-9 w-9 rounded-md hover:bg-[#FBEBEB] text-[#8E2E2E] flex items-center justify-center shrink-0"
                    aria-label="Quitar"
                    data-testid={`ocr-card-${idx}-remove`}
                  >
                    <Trash className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-[#8C8C8C]">Cantidad</Label>
                    <Input type="number" value={it.cantidad} onChange={(e) => ocrUpdate(it._key, { cantidad: e.target.value })} className="h-9 text-right" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-[#8C8C8C]">Unidad</Label>
                    <Input value={it.unidad} onChange={(e) => ocrUpdate(it._key, { unidad: e.target.value })} className="h-9" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-[#8C8C8C]">P. Unit.</Label>
                    <Input type="number" value={it.precio_unitario} onChange={(e) => ocrUpdate(it._key, { precio_unitario: e.target.value })} className="h-9 text-right font-mono" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-[#8C8C8C]">Descuento</Label>
                    <Input type="number" value={it.descuento || 0} onChange={(e) => ocrUpdate(it._key, { descuento: e.target.value })} className="h-9 text-right font-mono text-[#A6751F]" />
                  </div>
                </div>
                <div className="flex items-center justify-between bg-[#F9F6F0] rounded-md px-2 py-1.5">
                  <Label className="text-[10px] text-[#8C8C8C] mb-0">Total</Label>
                  <Input
                    type="number"
                    value={it.precio_total}
                    onChange={(e) => ocrUpdate(it._key, { precio_total: e.target.value })}
                    className="h-8 w-32 text-right font-mono font-bold text-[#C85A32] border-0 bg-transparent"
                  />
                </div>
              </div>
            ))}
          </div>

          <button type="button" onClick={ocrAdd} className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-[#E5E0D8] text-sm text-[#595959] hover:bg-[#F9F6F0]" data-testid="ocr-add">
            <Plus className="h-4 w-4" /> Agregar item manual
          </button>

          <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 text-sm">
            <div className="text-[#8C8C8C] text-[12px]">{ocrItems.length} item{ocrItems.length !== 1 ? "s" : ""} a guardar</div>
            <div className="font-display font-bold text-lg text-[#C85A32]">
              Subtotal: {formatMoney(ocrSubtotal)}
              {descGlobal > 0 && (
                <>
                  <span className="text-[12px] text-[#A6751F] font-sans font-normal mx-2">−{formatMoney(descGlobal)}</span>
                  <span> = {formatMoney(ocrTotal)}</span>
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOcrOpen(false)} className="gap-1.5">
              <ChevronLeft className="h-4 w-4" /> Cancelar
            </Button>
            <Button onClick={ocrSave} disabled={ocrSaving || ocrItems.length === 0} className="bg-[#C85A32] hover:bg-[#A64A29] text-white" data-testid="ocr-save">
              {ocrSaving ? "Guardando..." : "Confirmar y guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
